// Control Center Tenant 360 (Blueprint §4.4.1 / D-4) — bound to bccGetTenant360.
// One page, sticky identity header + tabs; data from the existing BCC callable.

import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SectionHeader, StatusBadge, StatBlock, ConfirmSheet } from "../../../components/control";
import {
  bccGetTenant360Callable,
  type BccTenant360Data,
  bccGrantEmergencyCreditsCallable,
  bccTransitionTenantLifecycleCallable,
  bccCreateOperationConfirmationCallable,
  listTenantCommunicationsCallable,
  resendTenantCommunicationCallable,
  type BccTenantCommunication,
} from "../../../lib/bccPhase1Callables";

type ActionKind = "grant" | "suspend" | null;

type Tab = "overview" | "commercial" | "credits" | "support" | "communications" | "audit";
const TABS: { id: Tab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "commercial", label: "Commercial" },
  { id: "credits", label: "AI Credits" },
  { id: "support", label: "Support" },
  { id: "communications", label: "Communications" },
  { id: "audit", label: "Audit" },
];

export function ControlTenant360Page() {
  const { firmId = "" } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<BccTenant360Data | null>(null);
  const [tab, setTab] = useState<Tab>("overview");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [communications, setCommunications] = useState<BccTenantCommunication[]>([]);
  const [communicationStatus, setCommunicationStatus] = useState("");

  const [action, setAction] = useState<ActionKind>(null);
  const [grantAmount, setGrantAmount] = useState(200);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const load = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    setError("");
    try {
      const [res, comms] = await Promise.all([
        bccGetTenant360Callable({ targetFirmId: firmId }),
        listTenantCommunicationsCallable({ targetFirmId: firmId, limit: 50 }).catch(() => ({ data: { communications: [] as BccTenantCommunication[] } })),
      ]);
      setData(res.data);
      setCommunications(comms.data.communications);
      setUpdatedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load tenant.");
    } finally {
      setLoading(false);
    }
  }, [firmId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runGrant = useCallback(async (reason: string) => {
    setActionBusy(true);
    setActionError("");
    try {
      let confirmationToken: string | undefined;
      if (grantAmount >= 1000) {
        const c = await bccCreateOperationConfirmationCallable({ operationType: "emergency_credit_grant", payload: { targetFirmId: firmId, amount: grantAmount }, reason });
        confirmationToken = c.data.confirmationToken;
      }
      await bccGrantEmergencyCreditsCallable({ targetFirmId: firmId, amount: grantAmount, reason, confirmationToken });
      setAction(null);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Grant failed.");
    } finally {
      setActionBusy(false);
    }
  }, [firmId, grantAmount, load]);

  const runSuspend = useCallback(async (reason: string) => {
    setActionBusy(true);
    setActionError("");
    try {
      // Destructive transition requires a confirmation token (payload key order
      // must match the server's createOperationPayloadHash exactly).
      const c = await bccCreateOperationConfirmationCallable({ operationType: "lifecycle_transition", payload: { targetFirmId: firmId, nextState: "suspended" }, reason });
      await bccTransitionTenantLifecycleCallable({ targetFirmId: firmId, nextState: "suspended", reason, confirmationToken: c.data.confirmationToken });
      setAction(null);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Suspend failed.");
    } finally {
      setActionBusy(false);
    }
  }, [firmId, load]);

  const resendCommunication = useCallback(async (notificationId: string) => {
    setCommunicationStatus("Re-enqueuing notification...");
    try {
      const result = await resendTenantCommunicationCallable({ targetFirmId: firmId, notificationId });
      setCommunicationStatus(`Notification re-enqueued: ${result.data.notificationId}`);
      await load();
    } catch (err) {
      setCommunicationStatus(err instanceof Error ? err.message : "Could not resend notification.");
    }
  }, [firmId, load]);

  const o = data?.overview;

  return (
    <div className="space-y-4">
      <button type="button" onClick={() => navigate("/control/tenants")} className="text-xs font-bold text-slate-500 hover:text-slate-900">‹ Tenants</button>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-black text-slate-900">{o?.firmName || firmId}</h1>
            <p className="text-[11px] text-slate-400">{firmId}</p>
          </div>
          <div className="flex items-center gap-2">
            {o ? <StatusBadge state={o.lifecycleState || o.subscriptionStatus} /> : null}
          </div>
        </div>
        {/* Action rail (D-6) — every mutation routes through a ConfirmSheet. */}
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => navigate(`/control/offers/new?firmId=${firmId}`)} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">Send proposal</button>
          <button type="button" onClick={() => setAction("grant")} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">Grant credits</button>
          <button type="button" onClick={() => setAction("suspend")} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">Suspend</button>
        </div>
      </div>

      <ConfirmSheet
        open={action === "grant"}
        title={`Grant credits — ${o?.firmName || firmId}`}
        description="Adds AI credits to this tenant's balance. Grants ≥ 1000 require step-up confirmation."
        busy={actionBusy}
        error={actionError}
        confirmLabel="Grant"
        onCancel={() => { setAction(null); setActionError(""); }}
        onConfirm={(reason) => void runGrant(reason)}
      >
        <label className="mt-3 flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
          Amount (credits)
          <input type="number" value={grantAmount} onChange={(e) => setGrantAmount(Number(e.target.value))} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal text-slate-900" />
        </label>
      </ConfirmSheet>

      <ConfirmSheet
        open={action === "suspend"}
        title={`Suspend tenant — ${o?.firmName || firmId}`}
        description="Pauses workspace access. The firm sees a clear notice, not a broken app."
        before={o?.lifecycleState}
        after="suspended"
        destructive
        requiresStepUp
        busy={actionBusy}
        error={actionError}
        confirmLabel="Suspend"
        onCancel={() => { setAction(null); setActionError(""); }}
        onConfirm={(reason) => void runSuspend(reason)}
      />

      <SectionHeader title="Tenant record" updatedAt={updatedAt} onRefresh={() => void load()} refreshing={loading} />
      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p> : null}

      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold ${tab === t.id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && o && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatBlock label="Lifecycle" value={o.lifecycleState || "—"} />
          <StatBlock label="Subscription" value={o.subscriptionStatus || "—"} />
          <StatBlock label="Offer type" value={o.offerType || "—"} />
          <StatBlock label="Monthly price" value={`AED ${o.finalMonthlyPriceAed ?? 0}`} hint={o.discountPercent ? `${o.discountPercent}% off` : undefined} />
        </div>
      )}

      {tab === "commercial" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <p className="mb-2 font-bold text-slate-900">Offers</p>
          {(data?.offers ?? []).length === 0 ? <p className="text-slate-400">No offers.</p> : (
            <ul className="space-y-1">
              {data?.offers.map((of) => (
                <li key={of.offerId} className="flex justify-between border-b border-slate-50 py-1">
                  <span>{of.offerType} · {of.status}</span>
                  <span className="text-slate-500">AED {of.finalMonthlyPriceAed} · {of.grossMarginPercent}% margin</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[11px] text-slate-400">Stripe: {o?.stripeCustomerId || "—"} / {o?.stripeSubscriptionId || "—"}</p>
        </div>
      )}

      {tab === "credits" && data?.aiCredits && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatBlock label="Balance" value={data.aiCredits.creditBalance} hint={data.aiCredits.lowCredit ? "low" : undefined} />
          <StatBlock label="Monthly included" value={data.aiCredits.monthlyIncludedCredits} />
          <StatBlock label="Cap" value={data.aiCredits.creditCap} />
        </div>
      )}

      {tab === "support" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          {(data?.support ?? []).length === 0 ? <p className="text-slate-400">No open tickets.</p> : (
            <ul className="space-y-1">
              {data?.support.map((s) => (
                <li key={s.ticketId} className="flex justify-between border-b border-slate-50 py-1">
                  <span>{s.title}</span>
                  <span className="text-slate-500">{s.priority} · {s.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "communications" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="font-bold text-slate-900">Delivery log</p>
              <p className="text-xs text-slate-400">Lifecycle emails queued through the notification outbox.</p>
            </div>
            {communicationStatus ? <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-600">{communicationStatus}</span> : null}
          </div>
          {communications.length === 0 ? <p className="text-slate-400">No lifecycle communications recorded.</p> : (
            <ul className="space-y-2">
              {communications.map((item) => {
                const canResend = item.status !== "queued" && item.status !== "sending";
                const tone = item.status === "sent"
                  ? "bg-emerald-100 text-emerald-700"
                  : item.status === "failed"
                    ? "bg-red-100 text-red-700"
                    : "bg-slate-100 text-slate-600";
                return (
                  <li key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-900">{item.templateKey || item.type || item.id}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-black uppercase ${tone}`}>{item.status}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{item.recipientEmail || "No recipient"} · attempts {item.attempts}</p>
                        <p className="mt-1 text-[11px] text-slate-400">
                          queued {item.createdAt ? new Date(item.createdAt).toLocaleString() : "pending"}
                          {item.deliveredAt ? ` · delivered ${new Date(item.deliveredAt).toLocaleString()}` : ""}
                          {item.lastAttemptAt && !item.deliveredAt ? ` · last attempt ${new Date(item.lastAttemptAt).toLocaleString()}` : ""}
                        </p>
                        {item.providerMessageId ? <p className="mt-1 text-[11px] text-slate-400">provider id {item.providerMessageId}</p> : null}
                        {item.error ? <p className="mt-2 rounded-md border border-red-100 bg-white px-2 py-1 text-xs font-semibold text-red-700">{item.error}</p> : null}
                      </div>
                      <button
                        type="button"
                        disabled={!canResend}
                        onClick={() => void resendCommunication(item.id)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 md:w-auto"
                      >
                        Resend
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {tab === "audit" && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          {(data?.audit ?? []).length === 0 ? <p className="text-slate-400">No audit events.</p> : (
            <ul className="space-y-1">
              {data?.audit.map((a) => (
                <li key={a.eventId} className="flex justify-between border-b border-slate-50 py-1">
                  <span className="font-mono text-xs">{a.eventType}</span>
                  <span className="text-slate-400">{a.createdAt ?? ""}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
